/* eslint-disable indent */
import { html, css, LitElement } from 'lit';
import { customElement, state, query } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import {
    loadScormItemsIfNeeded,
    publishScormItemAsComposition,
    unpublishScormItemAsComposition,
    createScormItem,
    deleteScormItem,
} from '../../store/scorm/scorm.js';
import { previewElearning } from '../../store/preview/preview.js';
import { navigate } from '../../store/route/route.js';
import { translate } from '../../helpers/translate.js';
import { renderTimestamp } from '../../helpers/controls.js';
import '@lit-labs/virtualizer';
import '@polymer/app-layout/app-layout.js';
import '@vaadin/context-menu/theme/material/vaadin-context-menu.js';
import '@material/web/fab/fab.js';
import '@sdb-develop/learning-frontend';
import '../shared/sdb-search-input.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-status-message.js';
import '../shared/sdb-dialog.js';
import './sdb-scorm-package-upload.js';

@customElement('sdb-scorm-item-list')
class SdbScormItemList extends connect(store)(LitElement) {

    @state()
    accessor scormItems = [];

    @state()
    accessor total;

    @state()
    accessor search;

    @state()
    accessor isLoadingFirstTime;

    @state()
    accessor user;

    @state()
    accessor args;

    @state()
    accessor newPackage = null;

    @state()
    accessor newName = null;

    @query('#scorm_uploader')
    accessor uploader;

    stateChanged(state) {
        this.scormItems = state.scorm.list || [];
        this.total = state.scorm.total;
        this.search = state.scorm.search;
        this.isLoadingFirstTime = state.scorm.status === 'loading' && state.scorm.page === -1;
        this.user = state.auth.user;
        this.portalId = state.env.portalIdentifier;
        this.hasGlobalId = state.env.hasGlobalId;
    }

    static styles = [
        sharedStyles,
        css`
            :host {
                max-width: var(--tcg-content-max-width);
                width: 100%;
            }

            .right-nav-bar {
                display:flex;
                align-items: center;
                justify-content: space-between;
            }

            sdb-search-input {
                flex-grow: 1;
            }

            sdb-content-iconbutton {
                margin-left: 10px;
            }

            #scorm_items_list {
                contain: none !important; /* lit-virtualizer hack */
            }

            sdb-content-listitem {
                width: 100%; /* lit-virtualizer screws this up */
            }



            .no-results {
                width: 100%;
                max-width: var(--tcg-content-max-width);
                height: 100%;
                display: flex;
                flex-direction: column;
                align-items: center;
            }

            #no_results_img {
                width: 400px;
                height: 400px;
                background-color: var(--tcg-secondary-background-color);
            }
        `,
    ];

    render() {
        if (this.isLoadingFirstTime) {
            return html`<sdb-loading></sdb-loading>`;
        }

        return html`
            <sdb-layout-60-40>
                <sdb-nav-titlebar slot="left">
                    <span>${translate('headerScormItems')}</span>
                    <span ?hidden=${!this.total}>&nbsp;(${this.total})</span>
                </sdb-nav-titlebar>
                <div class="right-nav-bar" slot="right">
                    <sdb-search-input
                        placeholder=${translate('search')}
                        value=${this.search}
                        @search=${(e) => { store.dispatch(loadScormItemsIfNeeded({ search: e.detail.searchText, reload: true })); }}>
                    </sdb-search-input>
                </div>
            </sdb-layout-60-40>
            <sdb-layout-100>
                ${!this.total
                    ? html`<sdb-status-message label=${translate('noScormItemsFound')}></sdb-status-message>`
                    : html`
                        <sdb-content-list separator>
                            <lit-virtualizer
                                id="scorm_items_list"
                                .items=${this.scormItems}
                                .renderItem=${(scormItem, index) => this._renderScormItemListItem(scormItem, index)}
                                .scrollTarget=${document}>
                            </lit-virtualizer>
                        </sdb-content-list>
                    `
                }

                <md-fab @click=${()=> { this._openDialog('add', null); }}>
                    <sdb-content-icon
                        slot="icon"
                        icon="fal fa-plus"
                        size="24">
                    </sdb-content-icon>
                </md-fab>
            </sdb-layout-100>

            <sdb-dialog
                type="confirm"
                id="publish_dialog"
                heading=${translate('publishHeadingScormItem')}
                body=${translate('publishBodyScormItem')}
                confirmCaption=${translate('publish')}
                @confirm=${(e) => { store.dispatch(publishScormItemAsComposition({ item: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="unpublish_dialog"
                heading=${translate('unpublishHeadingScormItem')}
                body=${translate('unpublishBodyScormItem')}
                confirmCaption=${translate('unpublish')}
                @confirm=${(e) => { store.dispatch(unpublishScormItemAsComposition({ item: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="confirm"
                id="delete_dialog"
                heading=${translate('deleteHeadingScormItem')}
                confirmCaption=${translate('delete')}
                @confirm=${(e) => { store.dispatch(deleteScormItem({ id: e.detail.args })); }}>
            </sdb-dialog>

            <sdb-dialog
                type="form"
                id="add_dialog"
                heading=${translate('createHeadingScormItem')}
                confirmCaption=${translate('create')}
                ?confirmDisabled=${!this.newName || !this.newPackage}
                @confirm=${() => {
                    store.dispatch(createScormItem({ name: this.newName, scormPackage: this.newPackage }))
                    this.newName = '';
                    if (this.uploader) this.uploader.clear();
                }}
                .bodyRenderer=${() => html`
                    <sdb-scorm-package-upload
                        id="scorm_uploader"
                        @upload-completed=${(e) => {
                            this.newPackage = e.detail.package;
                            this.newName = e.detail.manifestInfo?.title;
                        }}>
                    </sdb-scorm-package-upload>
                    <paper-input
                        id="scorm_new_name"
                        transparent
                        style="width: 700px;"
                        label=${translate('name')}
                        value=${this.newName}
                        @value-changed=${(e) => { this.newName = e.target.value; }}>
                    </paper-input>
                    <span style="display: block; margin-top: 4px; font-size: 12px; color: rgba(0, 0, 0, 0.6);">${translate('createScormItemHelper')}</span>
                `}>
            </sdb-dialog>
        `;
    }

    firstUpdated() {
        this.dispatchEvent(
            new CustomEvent('fullscreen', {
                bubbles: true,
                composed: true,
                detail: {
                    fullscreen: false,
                },
            }),
        );
    }

    _renderScormItemListItem(scormItem, index) {
        store.dispatch(loadScormItemsIfNeeded({ index }));

        const menuItems = [
            {
                text: translate('publish'),
                action: () => this._openDialog('publish', scormItem),
                hidden: this.hasGlobalId,
                disabled: scormItem.elearningType === 0,
            },
            {
                text: translate('unpublish'),
                action: () => this._openDialog('unpublish', scormItem),
                hidden: this.hasGlobalId,
                disabled: scormItem.elearningType === 1,
            },
            {
                text: translate('preview'),
                action: () => store.dispatch(previewElearning({ item: scormItem })),
            },
        ].filter(item => !item.hidden);

        return html`
            <sdb-content-listitem
                icon="fal fa-square-s"
                .headline=${scormItem.title}
                .subtitle=${this._getSubtitle(scormItem)}
                @click=${()=> { store.dispatch(navigate(`scormitem/${scormItem.targetId}`)); }}>

                <sdb-content-chipbar slot="chipbar">
                    <sdb-content-chip label="catalogus" ?hidden=${scormItem.elearningType !== 0}></sdb-content-chip>
                </sdb-content-chipbar>

                <vaadin-context-menu
                    slot="secondaryAction"
                    open-on="click"
                    .items=${menuItems}
                    @item-selected=${(e) => {
                        e.stopPropagation();
                        e.detail.value?.action?.();
                    }}>
                    <sdb-content-iconbutton icon="fal fa-ellipsis-vertical"></sdb-content-iconbutton>
                </vaadin-context-menu>

            </sdb-content-listitem>
        `;
    }

    _getSubtitle(scormItem) {
        var s = `${translate('lastModified')}: ${renderTimestamp(scormItem.lastModified)}`;
        if (scormItem.supplier && scormItem.supplier.supplierKey) {
            s += ` | ${scormItem.supplier.name}`;
        }
        if (scormItem.description) {
            s += ` | ${scormItem.description}`;
        }
        return s;
    }

    _openDialog(action, args) {
        this.renderRoot.querySelector(`#${action}_dialog`).open(args);
    }
}

